<template>
    <MainContent
        title="Listado de abonados"
        :value="filters"
        :meta="meta"
        @input="val => Object.assign(filters.value, val)"
    >
        <template #header>
            <Card customClasses="tw:mb-4">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-3 tw:justify-between">
                        <div class="tw:flex tw:w-full tw:items-center tw:justify-between tw:flex-col-reverse tw:md:flex-row tw:gap-4">
                            <!-- Buscador principal -->
                            <div class="tw:w-full">
                                <SearchComponent
                                    :value="filters.search"
                                    placeholder="Buscar"
                                    data-cy="search-abonados"
                                    @input="val => filters.search = val"
                                />
                            </div>
                            <div class="tw:flex tw:flex-col-reverse tw:md:flex-row tw:justify-end tw:gap-4 tw:w-full">
                                <Button
                                    id="add-abonado"
                                    icon="fas fa-plus"
                                    @click="showModal('addAbonado')"
                                    customClass="tw:text-xs tw:sm:text-sm tw:w-full tw:md:w-fit tw:justify-center"
                                >
                                    Añadir nuevo abonado
                                </Button>

                                <OptionsDropdownComponent
                                    :options="accionesOptions"
                                    label="Opciones"
                                    data-cy="opciones-buscador"
                                    customClass="tw:justify-center tw:text-xs tw:sm:text-sm tw:w-full tw:justify-center"
                                    containerClass="tw:w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="tw:flex tw:flex-col tw:gap-5">
                        <!-- Filtros secundarios -->
                        <AbonadosFilters
                            :filters="filters"
                            :tipos_abono="tipos_abono"
                            :metodos_pago="metodos_pago"
                            :configuracion="configuracion"
                            :disabled="disabled"
                            @reset="onResetFilters"
                            @update:filters="val => Object.assign(filters, val)"
                        />
                    </div>
                </div>
            </Card>

            <AbonadosList
                :abonados="abonados.data || []"
                :loading="loading"
                :columns="columns"
                :lista-id="lista_abonados ? lista_abonados.id : null"
                :teatro="teatro"
                :permisos="permisos"
                :configuracion="configuracion"
                :manager="manager"
                @enviar-abono="enviarAbono"
                @modificar-estado="modificarEstadoAbono"
                @show-modal="showModal"
            />
            <AnularAbono
                v-for="item in abonados.data"
                :key="`anular-${item.id}`"
                :abonado="item"
                v-on:close="closeModalAnular"
            />
            <BorrarAbono
                v-for="item in abonados.data"
                :key="`borrar-${item.id}`"
                :abonado="item"
                v-on:close="closeModalAnular"
            />

            <!-- Modales -->
            <AddAbonado
                v-if="lista_abonados"
                :lista_abonado="lista_abonados"
                v-on:close="closeModalAnular"
            />
            <EnviarAbonos
                :searchParams="searchParams()"
                v-on:close="closeModal"
            />
            <DescargarListaAbonados
                :searchParams="searchParams()"
                name="descargar-abonados"
                v-on:close="closeModal"
            />
        </template>
    </MainContent>
</template>

<script setup>
    import { computed, getCurrentInstance } from 'vue'
    import store from '@/store'
    import { config } from '@/config.js'

    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue'
    import AbonadosFilters from '@/modules/admin/abonados/components/AbonadosFilters.vue'
    import AbonadosList from '@/modules/admin/abonados/components/AbonadosList.vue'
    import AnularAbono from '@/modules/admin/abonados/modals/AnularAbono.vue'
    import BorrarAbono from '@/modules/admin/abonados/modals/BorrarAbono.vue'
    import EnviarAbonos from '@/modules/admin/abonados/modals/EnviarAbonos.vue'
    import AddAbonado from '@/modules/admin/abonados/modals/AddAbonado.vue'
    import DescargarListaAbonados from '@/modules/admin/abonados/modals/DescargarListaAbonados.vue'

    import { useAbonados } from '@/composables/admin/abonados/useAbonados'
    import { useToast } from '@/composables/common/useToast'

    const toast = useToast()
    const instance = getCurrentInstance()
    const vm = instance?.proxy

    const {
        loading,
        filters,
        abonados,
        meta,
        columns,
        tipos_abono,
        metodos_pago,
        lista_abonados,
        teatro,
        configuracion,
        permisos,
        manager,
        disabled,
        getAbonados,
        resetFilters,
        url_imprimir_abonos,
    } = useAbonados()

    // ==================== COMPUTED ====================
    const accionesOptions = computed(() => {
        const options = [
            {
                type: 'action',
                action: () => showModal('enviar-abonos'),
                label: 'Enviar Abonos',
                icon: 'envelope',
            },
            {
                type: 'url',
                url: url_imprimir_abonos.value,
                label: 'Imprimir',
                icon: 'print',
            },
            {
                type: 'action',
                action: () => showModal('descargar-abonados'),
                label: 'Descargar',
                icon: 'download',
            },
            ...(teatro.value?.familia_abonados_activa
                ? [{
                    to: { name: 'back.familias.index', params: { lista_id: store.getters.getListaId } },
                    label: 'Familias',
                    icon: 'users',
                }]
                : []),
        ]

        if (permisos.value?.['abonos.informe'] && manager.value?.lfp) {
            options.push({
                to: { name: 'back.abonos.informe' },
                label: 'VENTAS',
                icon: 'chart-bar',
            })
        }

        return options
    })

    // ==================== METHODS ====================
    function onResetFilters() {
        resetFilters()
    }

    function searchParams() {
        const f = filters.value
        const params = {
            lista_abonado_id: lista_abonados.value?.id,
            page: f.page,
            per_page: f.per_page,
        }

        if (f.search && f.search_for) params[f.search_for] = f.search
        if (f.tipo !== '') params.tipo = f.tipo
        if (f.metodo_pago) params.metodo_pago = f.metodo_pago
        if (f.tipo_abono) params.tipo_abono = f.tipo_abono
        if (f.tipo_precio) params.tipo_precio = f.tipo_precio
        if (f.activos !== '') params.activos = f.activos

        return { filters: params, lista_id: lista_abonados.value?.id }
    }

    function enviarAbono(id) {
        const params = { filters: {}, abonado_id: id }

        store.dispatch(config.getVuexAlias('back.abonados.abono.enviar'), params)
            .then(res => {
                toast.success('Abono enviado')
            })
            .catch(error => {
                console.error('Error enviando abono:', error)
                toast.error('Error al enviar el abono')
            })
    }

    function modificarEstadoAbono(abonado_id) {
        const params = { filters: {}, abonado_id: abonado_id }

        store.dispatch(config.getVuexAlias('back.abonados.abono.modificar-estado'), params)
            .then(res => {
                toast.success(res.data.message)
                getAbonados()
            })
            .catch(error => {
                console.error('Error modificando estado:', error)
                toast.error('Error al modificar el estado')
            })
    }

    function showModal(modal) {
        if (vm?.$modal) {
            vm.$modal.show(modal)
        }
    }

    function closeModal(modal) {
        if (vm?.$modal) {
            vm.$modal.hide(modal)
        }
    }

    function closeModalAnular(modal, anulado) {
        if (vm?.$modal) {
            vm.$modal.hide(modal)
        }
        if (anulado) {
            getAbonados()
        }
    }

</script>
